import { z } from 'zod';
import { scoreSchema } from './leaderboards';
import { manifestIdSchema } from './manifest';

/**
 * Multiplayer (M31, Phase 2): a game sets up its modes on the dashboard or with `mogzo modes`,
 * like leaderboards; the game starts one through the SDK and MOGZO finds the players, by quick
 * match or an invite code, and draws its own panels for it. Turns (M32), bases (M34) and
 * realtime rooms (M35) build on the match this makes.
 */

/** How a mode is played: turns on a shared state, attacks on saved bases, or a live room. */
export const MODE_KINDS = ['turns', 'bases', 'realtime'] as const;
export type ModeKind = (typeof MODE_KINDS)[number];
export const MODE_KIND_NAMES: Record<ModeKind, string> = {
  turns: 'Turns',
  bases: 'Bases',
  realtime: 'Realtime',
};

export const MULTIPLAYER_LIMITS = {
  /** Players in a turns match, and in a realtime room. Bases are one attacker and a defender. */
  turnsPlayers: 8,
  realtimePlayers: 16,
  /** An invite code works this long, while its match waits for players. */
  inviteMinutes: 30,
  /** Quick match widens its search for a close skill after this long, in seconds. */
  widenAfter: { min: 5, max: 120 },
  /** Time for a turn, in seconds: 30 seconds to 7 days. */
  turnSeconds: { min: 30, max: 7 * 24 * 60 * 60 },
  /** Realtime limits per player (M35). */
  messageRate: { min: 1, max: 20 },
  messageBytes: { min: 64, max: 2048 },
  roomMinutes: { min: 1, max: 60 },
  /** A quick match with enough players (but not full) starts after waiting this long. */
  quickStartSeconds: 20,
  /** Once a player sends results, the others have this long to send theirs. */
  resultSeconds: 60,
  /** A waiting player that hasn't checked in this long is taken out of the match. */
  goneSeconds: 15,
  /** How often MOGZO's panels ask how a waiting match is doing, in milliseconds. */
  pollMs: 2000,
  /** Where everyone's skill rating starts. */
  startRating: 1500,
} as const;

/** Bases (M34): one saved base per player and mode, attacked while its owner is away. */
export const BASES_LIMITS = {
  /** One base, as JSON. */
  baseBytes: 64 * 1024,
  /** After an attack, nobody can attack the base again for this long (a mode's setting). */
  shieldSeconds: { min: 0, max: 3 * 24 * 60 * 60 },
  /** The base is kept for its attacker this long; a later result is refused. */
  attackSeconds: { min: 30, max: 15 * 60 },
  attacksPerHour: { min: 1, max: 60 },
  /** The most loot a mode can allow from one attack, per kind of loot. */
  maxLoot: 1_000_000_000,
  /** Kinds of loot one attack can take (coins, gems…). */
  lootKinds: 8,
  /** A result this long after the attack's time is still taken, for slow networks. */
  graceSeconds: 15,
  /** An attack reported faster than this is flagged. */
  fastSeconds: 3,
  /** A player whose game called in this recently is playing, and their base isn't attacked. */
  playingSeconds: 120,
  /** A player refused by the hourly limit this many times in a day gets their attacks flagged. */
  limitHitsFlag: 3,
  /** Attacks a player's inbox shows, newest first, from this many days. */
  inbox: 50,
  keepDays: 30,
} as const;

/** Invite codes: 6 letters and digits, without ones easy to mix up (I, O, 0, 1). */
export const INVITE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const INVITE_CODE_LENGTH = 6;
const INVITE_PATTERN = /^[A-HJ-NP-Z2-9]{6}$/;

/** A code as a player typed it (any case, with spaces or dashes) in its stored form. */
export function normalizeInviteCode(text: string): string {
  return text.toUpperCase().replace(/[\s-]/g, '');
}
export const inviteCodeSchema = z
  .string()
  .max(20)
  .transform(normalizeInviteCode)
  .pipe(z.string().regex(INVITE_PATTERN, 'An invite code is 6 letters and digits, like K7QM2X'));

export const modeIdSchema = manifestIdSchema;

const whole = (min: number, max: number, what: string) =>
  z
    .int(`${what} is a whole number`)
    .min(min, `${what} is at least ${min}`)
    .max(max, `${what} is at most ${max}`);

const modeFieldsSchema = z.object({
  name: z.string().trim().min(1, 'Give the mode a name').max(30, 'A name can be up to 30 letters'),
  kind: z.enum(MODE_KINDS),
  minPlayers: whole(2, MULTIPLAYER_LIMITS.realtimePlayers, 'The fewest players'),
  maxPlayers: whole(2, MULTIPLAYER_LIMITS.realtimePlayers, 'The most players'),
  /** 0 for everyone on their own, 2 for two teams. */
  teams: z.union([z.literal(0), z.literal(2)]),
  quickMatch: z.boolean(),
  invites: z.boolean(),
  /** Quick match pairs players near each other's (hidden) rating. */
  skill: z.boolean(),
  widenAfter: whole(
    MULTIPLAYER_LIMITS.widenAfter.min,
    MULTIPLAYER_LIMITS.widenAfter.max,
    'Widen the search after',
  ),
  /** Turns (M32). */
  turnSeconds: whole(
    MULTIPLAYER_LIMITS.turnSeconds.min,
    MULTIPLAYER_LIMITS.turnSeconds.max,
    'Time for a turn',
  ),
  missedTurn: z.enum(['skip', 'forfeit']),
  /** Realtime (M35): who keeps the game going, and the room's limits. */
  host: z.enum(['everyone', 'host']),
  lateJoin: z.boolean(),
  messageRate: whole(
    MULTIPLAYER_LIMITS.messageRate.min,
    MULTIPLAYER_LIMITS.messageRate.max,
    'Messages a second',
  ),
  messageBytes: whole(
    MULTIPLAYER_LIMITS.messageBytes.min,
    MULTIPLAYER_LIMITS.messageBytes.max,
    'Message size',
  ),
  roomMinutes: whole(
    MULTIPLAYER_LIMITS.roomMinutes.min,
    MULTIPLAYER_LIMITS.roomMinutes.max,
    'Longest room',
  ),
  /**
   * Bases (M34): the shield after an attack, the time an attack has, attacks an hour per
   * player, and the most of each kind of loot one attack takes (null: no loot).
   */
  shieldSeconds: whole(
    BASES_LIMITS.shieldSeconds.min,
    BASES_LIMITS.shieldSeconds.max,
    'The shield',
  ),
  attackSeconds: whole(
    BASES_LIMITS.attackSeconds.min,
    BASES_LIMITS.attackSeconds.max,
    'Time for an attack',
  ),
  attacksPerHour: whole(
    BASES_LIMITS.attacksPerHour.min,
    BASES_LIMITS.attacksPerHour.max,
    'Attacks an hour',
  ),
  maxLoot: whole(0, BASES_LIMITS.maxLoot, 'The most loot').nullable(),
  /** A leaderboard that counts each player's wins, or null. */
  leaderboard: manifestIdSchema.nullable(),
});

/** Why a mode's settings don't fit together, or null when they do. */
export function modeProblem(mode: z.infer<typeof modeFieldsSchema>): {
  message: string;
  path: string;
} | null {
  const most =
    mode.kind === 'realtime' ? MULTIPLAYER_LIMITS.realtimePlayers : MULTIPLAYER_LIMITS.turnsPlayers;
  if (mode.kind === 'bases' && (mode.minPlayers !== 2 || mode.maxPlayers !== 2)) {
    return {
      message: 'A bases mode is one attacker and one defender: 2 players',
      path: 'maxPlayers',
    };
  }
  if (mode.maxPlayers > most) {
    return {
      message: `${MODE_KIND_NAMES[mode.kind]} modes hold up to ${most} players`,
      path: 'maxPlayers',
    };
  }
  if (mode.minPlayers > mode.maxPlayers) {
    return { message: 'The fewest players can’t be more than the most', path: 'minPlayers' };
  }
  if (mode.teams === 2 && mode.maxPlayers % 2 !== 0) {
    return { message: 'Two teams need an even number of players', path: 'maxPlayers' };
  }
  if (!mode.quickMatch && !mode.invites) {
    return { message: 'Choose quick match, invite codes or both', path: 'quickMatch' };
  }
  return null;
}

export const modeSettingsSchema = modeFieldsSchema.superRefine((mode, ctx) => {
  const problem = modeProblem(mode);
  if (problem) ctx.addIssue({ code: 'custom', message: problem.message, path: [problem.path] });
});
export type ModeSettings = z.infer<typeof modeSettingsSchema>;
/** Only the settings to change (the CLI's edit); checked again once merged. */
export const modeChangesSchema = modeFieldsSchema.partial();
export const newModeSchema = z.intersection(z.object({ id: modeIdSchema }), modeSettingsSchema);

/** A new mode's settings before the developer changes any. */
export function defaultModeSettings(kind: ModeKind, name = MODE_KIND_NAMES[kind]): ModeSettings {
  return {
    name,
    kind,
    minPlayers: 2,
    maxPlayers: kind === 'realtime' ? 4 : 2,
    teams: 0,
    quickMatch: true,
    invites: true,
    skill: true,
    widenAfter: 15,
    turnSeconds: 60,
    missedTurn: 'skip',
    host: 'everyone',
    lateJoin: false,
    messageRate: 15,
    messageBytes: 512,
    roomMinutes: 15,
    shieldSeconds: 8 * 60 * 60,
    attackSeconds: 3 * 60,
    attacksPerHour: 10,
    maxLoot: 500,
    leaderboard: null,
  };
}

/** A stored mode's settings with any made before a setting existed filled in (M34's bases). */
export function withModeDefaults(settings: ModeSettings): ModeSettings {
  return { ...defaultModeSettings(settings.kind, settings.name), ...settings };
}

/** A mode as the dashboard, the CLI and a published version see it. */
export type ModeView = ModeSettings & { id: string };

/**
 * Realtime opens to players once MOGZO moves its rooms to Cloudflare's paid plan (decided
 * Nov 1, 2026): until then realtime modes work in test only, and a version using one can't be
 * submitted. A super admin turns it on in admin (M35); the modes list says which modes are
 * test only (`testOnly`, `realtimeOpen`).
 */

// --- Matches -------------------------------------------------------------------------------

export const MATCH_STATUSES = ['waiting', 'playing', 'ended', 'cancelled'] as const;
export type MatchStatus = (typeof MATCH_STATUSES)[number];

/** One player in a match, as MOGZO's panels show them. */
export interface MatchPlayerView {
  /** Stable for this player in this game only. */
  playerId: string;
  name: string;
  me: boolean;
  host: boolean;
  /** 1 for the winner; null until the match has results. */
  place: number | null;
  score: number | null;
  /** Left a match that had started. */
  left: boolean;
}

/** A match as MOGZO's panels follow it, from waiting for players to the results. */
export interface MatchView {
  id: string;
  mode: Pick<ModeView, 'id' | 'name' | 'kind' | 'minPlayers' | 'maxPlayers'>;
  status: MatchStatus;
  /** Quick match, or made with an invite code (rematches too). */
  how: 'quick' | 'invite';
  /** The invite code while the match waits for players; null for quick match. */
  code: string | null;
  /** Where a friend opens the code, on mogzo.com. */
  inviteUrl: string | null;
  players: MatchPlayerView[];
  /** Seconds since the match was made. */
  waited: number;
  /** Whether every player's results are in (an ended match always has them). */
  results: boolean;
  /** Someone asked to play again: the new match, and how many have joined it. */
  rematch: { id: string; players: number } | null;
}

/** Whose turn it is in a turns match, and until when (ISO time). */
export interface MatchTurn {
  playerId: string;
  endsAt: string;
}

/**
 * What the game gets once its match starts, and when it opens one again later. Players are in
 * turn order. For turns modes it has the shared state, whose turn it is and how far along it is
 * (`seq`, the last event's number); other modes have none of these.
 */
export interface MatchStart {
  id: string;
  mode: string;
  status?: MatchStatus;
  players: { id: string; name: string; me: boolean; host: boolean; left?: boolean }[];
  state?: unknown;
  turn?: MatchTurn | null;
  moves?: number;
  seq?: number;
}

// --- Turns (M32) ---------------------------------------------------------------------------

export const TURN_LIMITS = {
  /** The shared state the game sends with a move, as JSON. */
  stateBytes: 32 * 1024,
  /** One move, as JSON. */
  moveBytes: 4 * 1024,
  /** Events (moves, skips, resignations) one match can have. */
  events: 5000,
  /**
   * How long a wait for the next move is held open before it answers anyway, in seconds. A
   * move answers it at once; each wait that runs out costs a call, so longer is cheaper (M36).
   */
  waitSeconds: 25,
} as const;

/** What happened in a turns match: a move, or MOGZO skipping or forfeiting a turn, or a resignation. */
export const MATCH_EVENT_KINDS = ['move', 'skipped', 'forfeited', 'resigned'] as const;
export type MatchEventKind = (typeof MATCH_EVENT_KINDS)[number];

export interface MatchEvent {
  seq: number;
  kind: MatchEventKind;
  /** Who moved, or whose turn was skipped, forfeited or resigned. */
  playerId: string | null;
  /** The game's move, for `move` events. */
  move: unknown;
  at: string;
}

/** A turns match as the game follows it: what changed after `seq`, and where it is now. */
export interface TurnUpdate {
  match: MatchStart;
  events: MatchEvent[];
  /** The match ended (results are in) or was called off. */
  over: boolean;
}

/** A player's match that's still going, for `mogzo.matches.mine()`. */
export interface OpenMatch {
  id: string;
  mode: string;
  modeName: string;
  status: MatchStatus;
  players: { id: string; name: string; me: boolean; host: boolean; left?: boolean }[];
  turn: MatchTurn | null;
  myTurn: boolean;
  moves: number;
  lastMoveAt: string | null;
}

/** Bridge parameters for the turn calls. */
export const moveParamsSchema = z
  .object({
    match: z.uuid(),
    move: z.json(),
    state: z.json(),
    /** The number of moves the game last saw; a move made on an older state is refused. */
    expect: z.int().min(0).optional(),
    /** The player moves again: the turn stays with them. */
    keepTurn: z.boolean().optional(),
  })
  .strict();
export const waitParamsSchema = z
  .object({
    match: z.uuid(),
    after: z.int().min(0),
    /** Answer at once, without waiting for the next move. */
    now: z.boolean().optional(),
  })
  .strict();
export const openMatchParamsSchema = z.object({ match: z.uuid() }).strict();

/** What the game gets from `match.end()`: what the player chose on the results panel. */
export type MatchNext =
  | { action: 'rematch'; match: MatchStart }
  | { action: 'new'; match: MatchStart }
  | { action: 'done' };

/**
 * A match's results, from a player's game: who won (null for a draw), the players in order
 * best first, or scores (higher is better). A game can send only its own player's score; the
 * match ends when every score is in, or a minute after the first.
 */
export const matchResultSchema = z
  .object({
    winner: z.string().max(64).nullable().optional(),
    ranking: z.array(z.string().max(64)).min(1).max(16).optional(),
    scores: z.record(z.string().max(64), scoreSchema).optional(),
  })
  .strict()
  .refine(
    (result) =>
      result.winner !== undefined || result.ranking !== undefined || result.scores !== undefined,
    'Send a winner, a ranking or scores',
  );
export type MatchResult = z.infer<typeof matchResultSchema>;

/** Bridge parameters for the `multiplayer.*` methods. */
export const findMatchParamsSchema = z.object({ mode: modeIdSchema }).strict();
export const createMatchParamsSchema = z
  .object({ mode: modeIdSchema, invite: z.boolean().default(true) })
  .strict();
export const joinMatchParamsSchema = z.object({ code: inviteCodeSchema.optional() }).strict();
export const endMatchParamsSchema = z
  .object({ match: z.uuid(), result: matchResultSchema })
  .strict();
export const leaveMatchParamsSchema = z.object({ match: z.uuid() }).strict();

// --- Rooms (M35) ---------------------------------------------------------------------------

/** A ticket into a realtime match's room: the game connects to `url` and sends it first. */
export interface RoomTicket {
  room: string;
  url: string;
  ticket: string;
  expiresAt: string;
}

/**
 * How a room's connection is going, from the game: dropped (MOGZO shows "Reconnecting…"), back,
 * or gone for good (its place wasn't kept long enough).
 */
export const ROOM_CONNECTION_STATES = ['lost', 'back', 'gone'] as const;
export type RoomConnectionState = (typeof ROOM_CONNECTION_STATES)[number];

/** What the player chose on MOGZO's room panel. */
export type RoomChoice = { action: 'leave' | 'back' | 'find' | 'done' };

export const roomTicketParamsSchema = z.object({ match: z.uuid() }).strict();
export const roomConnectionParamsSchema = z
  .object({
    match: z.uuid().nullable(),
    state: z.enum(ROOM_CONNECTION_STATES),
    /** For `lost`: when the place stops being kept (ms since 1970). */
    until: z.number().optional(),
  })
  .strict();

// --- Bases (M34) ---------------------------------------------------------------------------

/** What one attack took, by kind (`{ coins: 40 }`): whole numbers, up to 8 kinds. */
export const lootSchema = z
  .record(
    z.string().regex(/^[A-Za-z][A-Za-z0-9_-]{0,31}$/, 'Name loot with letters, digits, - and _'),
    z.int('Loot is a whole number').min(0).max(BASES_LIMITS.maxLoot),
  )
  .refine(
    (loot) => Object.keys(loot).length <= BASES_LIMITS.lootKinds,
    `An attack takes up to ${BASES_LIMITS.lootKinds} kinds of loot`,
  );
export type Loot = z.infer<typeof lootSchema>;

/** How an attack went, from the attacker's game. */
export const attackResultSchema = z
  .object({ won: z.boolean(), score: scoreSchema.optional(), loot: lootSchema.optional() })
  .strict();
export type AttackResult = z.infer<typeof attackResultSchema>;

/** Why MOGZO marked an attack for the developer to look at. */
export type AttackFlag =
  | { kind: 'loot_cut'; loot: string; sent: number }
  | { kind: 'too_fast'; seconds: number }
  | { kind: 'hourly_limit'; times: number };

/** The player's own base, for `mogzo.bases.mine()`. */
export interface MyBase {
  mode: string;
  base: unknown;
  savedAt: string;
  /** Nobody can attack it until then (ISO time); null when it isn't shielded. */
  shieldUntil: string | null;
}

/** A base to attack, from `mogzo.bases.findOpponent()`. Report how it went by its `id`. */
export interface BaseOpponent {
  id: string;
  mode: string;
  player: { id: string; name: string };
  base: unknown;
  /** Report the result before then (ISO time). */
  endsAt: string;
}

/** An attack as its attacker's game gets it back. */
export interface AttackView {
  id: string;
  mode: string;
  defender: { id: string; name: string };
  won: boolean;
  score: number | null;
  loot: Loot;
  at: string;
}

/** An attack on the player's base, for `mogzo.bases.inbox()`. `new` until the game has seen it. */
export interface InboxAttack {
  id: string;
  mode: string;
  attacker: { id: string; name: string };
  won: boolean;
  score: number | null;
  loot: Loot;
  at: string;
  new: boolean;
}

/** Bridge parameters for the `bases.*` methods. */
export const saveBaseParamsSchema = z.object({ mode: modeIdSchema, base: z.json() }).strict();
export const baseModeParamsSchema = z.object({ mode: modeIdSchema }).strict();
export const reportAttackParamsSchema = z
  .object({ attack: z.uuid(), result: attackResultSchema })
  .strict();

/** Loot held to a mode's most per kind (null: no loot); what was cut is flagged. */
export function holdLoot(sent: Loot, most: number | null) {
  const loot: Loot = {};
  const flags: AttackFlag[] = [];
  for (const [kind, amount] of Object.entries(sent)) {
    const allowed = Math.min(amount, most ?? 0);
    if (allowed < amount) flags.push({ kind: 'loot_cut', loot: kind, sent: amount });
    if (allowed > 0) loot[kind] = allowed;
  }
  return { loot, flags };
}
